/**
 * `getLicensingPath` client (#411).
 *
 * PUBLIC + unauthenticated by design on the backend: it is published board rule
 * data, and the onboarding step that needs it runs BEFORE an account exists.
 *
 * The client derives nothing. It asks with what the learner has answered so far
 * and renders the strings that come back, so web and iOS can never disagree
 * about whether a state needs the exam first.
 */
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase';
import { callWithTimeout } from '@/utils/callWithTimeout';
import { logger } from '@/utils/logger';
import type { LicenseRung, LicensingPath, ProcessStage } from './types';

export interface LicensingPathQuery {
  jurisdiction: string;
  licenseRung?: LicenseRung;
  processStage?: ProcessStage;
}

/**
 * Per-session memo. The endpoint is STATELESS (bundled rulebook, no clock, no
 * user state), so the same question always has the same answer for the life of
 * the tab, and the step re-asks it on every answer.
 */
const cache = new Map<string, Promise<LicensingPath>>();

function cacheKey(query: LicensingPathQuery): string {
  return [query.jurisdiction, query.licenseRung ?? '', query.processStage ?? ''].join('|');
}

export function fetchLicensingPath(query: LicensingPathQuery): Promise<LicensingPath> {
  const key = cacheKey(query);
  const hit = cache.get(key);
  if (hit) return hit;

  // OMIT, never send undefined: the callable protocol serializes an undefined
  // property as JSON `null`, and the backend contract rejects a null rung with
  // "Expected 'bachelors' | …, received null" — so an unanswered question would
  // 400 the whole lookup instead of defaulting.
  const payload: LicensingPathQuery = { jurisdiction: query.jurisdiction };
  if (query.licenseRung) payload.licenseRung = query.licenseRung;
  if (query.processStage) payload.processStage = query.processStage;

  const request = callWithTimeout(
    () =>
      httpsCallable<LicensingPathQuery, LicensingPath>(
        functions,
        'getLicensingPath'
      )(payload).then((r) => r.data),
    { timeoutMs: 10000, retries: 1 }
  ).catch((error) => {
    // A failed lookup must not be remembered as an answer.
    cache.delete(key);
    logger.warn('licensingApi', 'getLicensingPath failed', { error, jurisdiction: query.jurisdiction });
    throw error;
  });

  cache.set(key, request);
  return request;
}

/** Test seam: the memo outlives a component, so specs clear it between cases. */
export function __clearLicensingPathCache(): void {
  cache.clear();
}
